import { usePage } from "@inertiajs/react";
import { Client } from "@twilio/conversations";
import { useEffect, useState } from "react";
import { MessageComponentPrefix } from "../Components/Breadcrumb/Utils/MessageBreadcrumb";
import { ConversationEvent, ConversationUpdatedReason, MessagePageEvent, TwilioClientEvent } from "../Constants/twilio";
import { countUnreadConversationData, getConversations, getLastMessageFromTwilioConversation, getTwilioToken } from "../Services/TwilioClientService";

export const useTwilioClient = () => {
    const { auth: { user } } = usePage().props;
    const [userId] = useState(user?.data.id);
    const { component } = usePage();
    const [twilioToken, setTwilioToken] = useState(null);
    const [twilioClient, setTwilioClient] = useState(null);
    const [unreadCount, setUnreadCount] = useState({
        all: 0,
        apply: 0,
        scout: 0,
    });
    const [twilioConversation, setTwilioConversation] = useState({
        loaded: false,
        data: [],
    });
    const [clientFirstChange, setClientFirstChange] = useState(false);
    const [firstAddEventSync, setFirstAddEventSync] = useState(true);

    const getTwilioConversations = async () => {
        if (!twilioClient) {
            return [];
        }

        const conversations = await getConversations({ client: twilioClient });

        setTwilioConversation({
            loaded: true,
            data: [...conversations],
        });

        return conversations;
    };

    const getUnreadConversationCount = async () => {
        const conversations = await getTwilioConversations();

        const unreadData = await countUnreadConversationData({ conversations, userUid: user?.data?.uid });

        setUnreadCount({
            ...unreadData
        });
    };

    useEffect(() => {
        const createToken = async () => {
            if (!user) {
                setTwilioToken(null);
                return;
            }

            const response = await getTwilioToken({ uid: user.data.uid });

            setTwilioToken(response.data);
        };

        createToken();
    }, [userId]);

    const resetTwilioClient = async () => {
        twilioClient && await twilioClient.shutdown();
    };

    useEffect(() => {
        const handleInitTwilioClient = async () => {
            if (!user || !twilioToken) {
                twilioClient && await twilioClient.shutdown();
                setTwilioClient(null);
                return;
            }

            const client = new Client(twilioToken);

            client.on(TwilioClientEvent.initialized, () => {
                setTwilioClient(client);
            });
        };

        handleInitTwilioClient();
    }, [twilioToken]);

    useEffect(() => {
        if (twilioClient && !clientFirstChange) {
            setClientFirstChange(true);

            getUnreadConversationCount();


            /***** Token Expire Event *****/
            twilioClient.on(TwilioClientEvent.tokenAboutToExpire, async () => {
                if (!user) {
                    return;
                }

                const response = await getTwilioToken({ uid: user.data.uid });

                await twilioClient.updateToken(response.data);
            });
            twilioClient.on(TwilioClientEvent.tokenExpired, async () => {
                setClientFirstChange(false);
                await twilioClient.shutdown();

                const response = await getTwilioToken({ uid: user.data.uid });

                setTwilioToken(response.data);
            });


            /***** New Conversation Joined *****/
            var lastAddedEvent = Math.floor(Date.now() / 1000);

            twilioClient.on(TwilioClientEvent.conversationJoined, (conversation) => {
                if (!firstAddEventSync) {
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent(MessagePageEvent.conversationJoined, {
                            detail: { conversation: conversation },
                        }));
                    }, 2000);
                } else {
                    // If more than 1s => finished sync before
                    let currentTime = Math.floor(Date.now() / 1000);

                    if (currentTime - lastAddedEvent > 1) {
                        setFirstAddEventSync(true);
                        setTimeout(() => {
                            window.dispatchEvent(new CustomEvent(MessagePageEvent.conversationJoined, {
                                detail: { conversation: conversation },
                            }));
                        }, 2000);
                    }

                    lastAddedEvent = currentTime;
                }
            });

            twilioClient.on(TwilioClientEvent.conversationUpdated, (event) => {
                const eventConversation = event.conversation;

                /***** Last Message Reason ******/
                if (event.updateReasons[0] === ConversationUpdatedReason.lastMessage) {

                    window.dispatchEvent(new CustomEvent(ConversationUpdatedReason.lastMessage, {
                        detail: {
                            conversation: eventConversation,
                        },
                    }));
                }

                /***** Last Read Index ******/
                if (event.updateReasons[0] === ConversationUpdatedReason.lastReadMessageIndex) {
                    window.dispatchEvent(new CustomEvent(MessagePageEvent.lastReadMessageIndex, {
                        detail: { conversation: eventConversation },
                    }));
                }
            });

            /***** CONVERSATION EVENT *****/
            twilioClient.on(ConversationEvent.messageAdded, (message) => {
                window.dispatchEvent(new CustomEvent(MessagePageEvent.messageAdded, {
                    detail: { message },
                }));
            });
        }
    }, [twilioClient]);

    useEffect(() => {
        const handleLastMessage = async (e) => {
            if (component.startsWith(MessageComponentPrefix)) {
                /***** Event Last Message for Message Page ******/
                window.dispatchEvent(new CustomEvent(MessagePageEvent.lastMessage, {
                    detail: { conversation: e.detail.conversation },
                }));
            } else {
                const conversation = e.detail.conversation;
                const lastMessage = await getLastMessageFromTwilioConversation({ conversation });

                const isMe = lastMessage?.author === user.data.uid;

                // Check case app send message and not update lastReadIndex
                if (isMe && conversation.lastReadMessageIndex < lastMessage.index) {
                    await conversation.setAllMessagesRead();
                }

                !isMe && getUnreadConversationCount();
            }
        };

        window.addEventListener(ConversationUpdatedReason.lastMessage, handleLastMessage);

        return () => {
            window.removeEventListener(ConversationUpdatedReason.lastMessage, handleLastMessage);
        };
    }, [component, user, twilioClient]);

    return { twilioToken, twilioClient, unreadCount, twilioConversation, getUnreadConversationCount, setUnreadCount, resetTwilioClient };
};
